import { I18N } from '@/app'
import { AppStatus, useAppSelector } from '@/redux'
import { useI18N } from '@codeleap/common'
import { useLocation } from '@reach/router'
import type Firebase from 'firebase/auth'
import { navigate } from 'gatsby'
import { getFirebase } from '../../firebaseApp'
import { ensureLoggedInTopic } from './ensureProfileCompletion'
import { useLinkedInAuth } from './linkedIn'
import { QueryKeys } from './queryKeys'
import { linkedInAuth, Profile, retrieve } from './views'

const SCOPE = 'Social Authentication'

type FirebaseUser = Firebase.User

type SocialLoginResult = {
  customToken?: string
  credential?: Firebase.AuthCredential
  extraData?: Partial<FirebaseUser> & { error: any }
}

export const CredentialProviders = {
  google: async (): Promise<SocialLoginResult> => {
    try {
      const firebase = await getFirebase()
      const res = await firebase.auth().signInWithPopup(new firebase.auth.GoogleAuthProvider())

      return {
        credential: res?.credential,
      }
    } catch (err) {
      logger.error({ err })
    }
  },
  facebook: async (): Promise<SocialLoginResult> => {
    try {
      const firebase = await getFirebase()
      const provider = new firebase.auth.FacebookAuthProvider()

      provider.addScope('email')
      provider.addScope('public_profile')

      provider.setCustomParameters({
        display: 'popup',
      })

      const res = await firebase.auth().signInWithPopup(provider)

      return {
        credential: res?.credential,
      }
    } catch (err) {
      logger.error({ err })

      if (err?.code === 'auth/account-exists-with-different-credential') {
        alert(I18N.t('Login.accountWithOtherProvider.body'))
      }
    }
  },
  apple: async (): Promise<SocialLoginResult> => {
    try {
      const firebase = await getFirebase()
      const provider = new firebase.auth.OAuthProvider('apple.com')

      provider.addScope('email')
      provider.addScope('name')

      const res = await firebase.auth().signInWithPopup(provider)

      return {
        credential: res?.credential,
      }
    } catch (err) {
      logger.error({ err })
    }
  },
  linkedIn: async (linkedinToken: string): Promise<SocialLoginResult> => {
    const { profile, token } = await linkedInAuth(linkedinToken)

    return {
      customToken: token,
      extraData: {
        displayName: profile?.localizedFirstName + ' ' + profile?.localizedLastName,
        email: profile?.email,
      },
      credential: null,
    }
  },
}

export type SocialProviderTypes = keyof typeof CredentialProviders

type User = Partial<Pick<Profile, 'first_name' | 'last_name' | 'email' | 'id'>>

export const authProvidersList = Object.keys(CredentialProviders) as SocialProviderTypes[]

export type TrySocialLoginArgs = {
  withProvider?: SocialProviderTypes
}

type SocialLoginOptions = {
  provider: SocialProviderTypes
  params?: any
  type?: 'login' | 'signup'
}

type SocialLoginReturn = {
  alreadyExistsInBackend: boolean
  user?: User | null
}

export async function socialLogin(options: SocialLoginOptions): Promise<SocialLoginReturn> {
  try {
    const { provider, params = {}, type } = options

    AppStatus.set('loading')

    let firebaseUser: FirebaseUser = null

    let extraData: SocialLoginResult['extraData'] = null

    const firebase = await getFirebase()

    const socialCredential = await CredentialProviders[provider](params)

    extraData = socialCredential?.extraData

    let firebaseCredential = null

    if (socialCredential?.customToken) {
      firebaseCredential = await firebase.auth().signInWithCustomToken(socialCredential.customToken)
    } else {
      firebaseCredential = await firebase.auth().signInWithCredential(socialCredential.credential)
    }

    firebaseUser = firebaseCredential?.user

    let profile = null

    try {
      profile = await retrieve()
    } catch (e) {
      logger.info('Failed to get profile from API during socialLogin ' + provider, e, SCOPE)
    }

    const userExistsOnBackend = !!profile

    if (userExistsOnBackend) {
      await QueryKeys.me.refresh()
      AppStatus.authFinished()
      ensureLoggedInTopic.publish(true)
      AppStatus.set('done')
      return {
        alreadyExistsInBackend: true,
        user: profile,
      }
    }

    if (firebaseUser) {
      const user: User = {
        id: firebaseUser?.uid,
        email: firebaseUser?.email,
      }

      if (provider === 'facebook') {
        user.email = firebaseUser?.providerData?.[0]?.email
      }

      const nameParts = (firebaseUser?.displayName || extraData?.displayName)?.split?.(' ') || ['', '']

      user.first_name = nameParts?.[0]

      if (nameParts.length > 1) {
        user.last_name = nameParts?.[1]
      }

      if (nameParts.length > 0) {
        user.name = nameParts?.join?.(' ')
      }

      if (type !== 'signup') AppStatus.set('idle')

      ensureLoggedInTopic.publish(true)

      return {
        alreadyExistsInBackend: false,
        user: user,
      }
    }
  } catch (e) {
    AppStatus.set('idle')

    if (e?.code === '12501' || e?.code === 'EUNSPECIFIED') return // signup cancel codes google and apple

    logger.error('Error on socialLogin ' + options?.provider, e, SCOPE)

    return {
      alreadyExistsInBackend: false,
      user: null,
    }
  }
}

export const useSocialLogin = (
  handle: (user: User, provider: SocialProviderTypes) => void,
  type: 'login' | 'signup',
) => {
  const isModal = useAppSelector(state => state.AppStatus.modals?.[type] || state.AppStatus.modals?.authWall)
  const location = useLocation()

  const isLogin = location?.pathname?.includes('login')

  const { t } = useI18N()

  const checkSocialAction = (socialResult: SocialLoginReturn) => {
    const { user, alreadyExistsInBackend } = socialResult

    if (isLogin && !alreadyExistsInBackend) {
      if (!!user?.id) {
        alert(t('Login.socialWarn'))

        if (isModal) {
          AppStatus.transitionModals(['login', 'signup'])
        } else {
          navigate('/auth/signup')
        }
      }

      return false
    } else if (!isLogin && alreadyExistsInBackend) {
      if (isModal) {
        AppStatus.setModal(['signup', false])
        AppStatus.setModal(['login', false])

        QueryKeys.me.setData(user as Profile)
        QueryKeys.me.refresh()
      } else {
        navigate('/')
      }

      return false
    }

    return true
  }

  const checkSocialResult = (socialResult: SocialLoginReturn, provider: SocialProviderTypes) => {
    const checked = checkSocialAction(socialResult)

    if (checked) {
      handle?.(socialResult?.user, provider)
    }
  }

  const linkedInAuth = useLinkedInAuth(result => checkSocialResult(result, 'linkedIn'))

  const handleSocial = async (provider: SocialProviderTypes) => {
    if (provider === 'linkedIn') {
      linkedInAuth()
      return
    }

    const socialResult = await socialLogin({ provider, type })

    checkSocialResult(socialResult, provider)
  }

  return handleSocial
}
